import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtCurrency, fmtHours, fmtRelative } from "@/lib/format";
import { num, rows, type LabelValue, type NameValue } from "@/lib/kpi";
import {
  BarList,
  ErrorState,
  GapList,
  KpiCard,
  Leaderboard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

type BranchRow = { name: string; code: string; new: number; open: number; completed: number };
type DeviceBrandRow = { brand: string; tickets: number; completed: number };
type DeviceModelRow = { brand: string; model: string; category: string | null; tickets: number; completed: number };
type PopularRepairRow = { name: string; jobs: number; value: number };
type DeviceMix = { brands?: DeviceBrandRow[]; models?: DeviceModelRow[]; popular_repairs?: PopularRepairRow[] };

export default function RepairsPage() {
  const { range } = useFilters();
  const q = useQuery({ queryKey: ["src", "repairs", range], queryFn: () => dashApi.source("repairs", range) });
  const k = q.data?.kpis;
  const dm = (k?.device_mix ?? {}) as DeviceMix;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Repairs"
        description="IRONCore repair-ticket operations."
        right={q.data && <div className="flex items-center gap-2"><StatusPill status={q.data.status} /><span className="text-xs text-slate-500">{fmtRelative(q.data.captured_at)}</span></div>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="New" value={num(k, "new_tickets")} unit="count" />
            <KpiCard label="Open" value={num(k, "open_tickets")} unit="count" />
            <KpiCard label="Completed" value={num(k, "completed_tickets")} unit="count" delta={{ pct: pctDelta(num(k, "completed_tickets"), num(k, "prev_completed_tickets")) }} />
            <KpiCard label="Collected" value={num(k, "collected_tickets")} unit="count" />
            <KpiCard label="Overdue" value={num(k, "overdue_tickets")} unit="count" tone="danger" />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Panel title="Avg completion time">
              <p className="text-3xl font-semibold text-white">{fmtHours(num(k, "avg_completion_hours"))}</p>
              <p className="mt-1 text-xs text-slate-500">Mean time from creation to completion over the window.</p>
            </Panel>
            <Panel title="Open by status">
              <BarList rows={rows<LabelValue>(k, "status_breakdown")} unit="count" />
            </Panel>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Top closers">
              <Leaderboard rows={rows<NameValue>(k, "top_closers")} unit="count" />
            </Panel>
            <Panel title="Branch split">
              <BranchTable rows={rows<BranchRow>(k, "branch_split")} />
            </Panel>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Devices in for repair — top models" subtitle="Tickets in the selected range by device model, with how many completed.">
              <DeviceModelTable rows={dm.models ?? []} />
            </Panel>
            <div className="space-y-6">
              <Panel title="By brand">
                <BarList rows={(dm.brands ?? []).map((b) => ({ label: b.brand, value: b.tickets }))} unit="count" empty="No devices in range." />
              </Panel>
              <Panel title="Popular repairs" subtitle="Repair (labour) lines on quotes approved in the range — what customers actually buy.">
                <PopularRepairsTable rows={dm.popular_repairs ?? []} />
              </Panel>
            </div>
          </div>
          {q.data && <GapList gaps={q.data.gaps} />}
        </>
      )}
    </div>
  );
}

function pctDelta(cur: number | null, prev: number | null): number | null {
  if (cur === null || prev === null || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}

function DeviceModelTable({ rows }: { rows: DeviceModelRow[] }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No devices in range.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Device</th>
          <th className="pb-2 font-medium">Category</th>
          <th className="pb-2 text-right font-medium">Tickets</th>
          <th className="pb-2 text-right font-medium">Completed</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r, i) => (
          <tr key={i} className="text-slate-300">
            <td className="py-2"><span className="text-slate-100">{r.brand} {r.model}</span></td>
            <td className="py-2 text-[12px] text-slate-500">{r.category ?? "—"}</td>
            <td className="py-2 text-right tabular-nums">{r.tickets}</td>
            <td className="py-2 text-right tabular-nums">{r.completed}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PopularRepairsTable({ rows }: { rows: PopularRepairRow[] }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No approved repairs in range.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Repair</th>
          <th className="pb-2 text-right font-medium">Jobs</th>
          <th className="pb-2 text-right font-medium">Value</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r, i) => (
          <tr key={i} className="text-slate-300">
            <td className="py-2">{r.name}</td>
            <td className="py-2 text-right tabular-nums">{r.jobs}</td>
            <td className="py-2 text-right tabular-nums">{fmtCurrency(r.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BranchTable({ rows }: { rows: BranchRow[] }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No branch data.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Branch</th>
          <th className="pb-2 text-right font-medium">New</th>
          <th className="pb-2 text-right font-medium">Open</th>
          <th className="pb-2 text-right font-medium">Completed</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r) => (
          <tr key={r.code} className="text-slate-300">
            <td className="py-2">{r.name}</td>
            <td className="py-2 text-right tabular-nums">{r.new}</td>
            <td className="py-2 text-right tabular-nums">{r.open}</td>
            <td className="py-2 text-right tabular-nums">{r.completed}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
